/**
 * @render react
 * @name Social
 * @example
 * mode: brand page
 * <Social platforms={[{ label: 'Follow us on Facebook', url: 'https://www.facebook.com/evomagazine', platform: 'Facebook', },{ label: 'Follow us on Twitter', url: 'https://twitter.com/evomagazine', platform: 'Twitter', }, {label: 'Follow us on Youtube', url: 'https://youtube.com/evomagazine', platform: 'youtube',}, {label: 'Follow us on Instagram', url: 'https://instagram.com/evomagazine', platform: 'instagram',},]}  />
 * mode: share url
 * <Social url="/article/article-title" title="Article title" image="https://place-hold.it/600/0000ff" platforms={[{ label: 'Share on Facebook', platform: 'Facebook', },{ label: 'Share on Twitter', platform: 'Twitter', }, {label: 'Share on Instagram', url: 'https://instagram.com/evomagazine', platform: 'instagram',},]} />
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';
import uniqid from 'uniqid';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Link } from 'Components/molecules/Link';

import renderAmp from './amp';

import './_style.scss';

/**
 * Social component
 * @param {string} url -  URL to share to the platforms. Optional. For e.g http://www.evo.co.uk
 * @param {string} title - Title of the content we want to share. Optional.
 * @param {string} image - url of the image source. Optional.
 * @param {array} platforms - Array of supported platforms. Each item in the array will be an object with:
 *                            - label - Text to display
 *                            - url - If URL is blank, then the URL prop will be used. This is useful if we don’t want to share content but just redirect the
 *                            user to the brand social page. For e.g https://www.facebook.com/evomagazine
 *                            - platform - facebook/instagram/twitter/pinterest/youtube/email
 * @param {Function} onShared - Callback when a content is shared (depending on platforms where it is supported)
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} context - Extra information coming from Polaris provider.
 */
class Social extends Component {
  /**
   * onClick event handle
   * @param {*} e - The event fired
   * @param {string} url - URL to share to the platforms
   * @param {string} title - title of the content we want to share
   * @param {string} platform - name of the social platform
   * @param {string} twitterHandle
   */
  static handleClick(e, url, title, platform, twitterHandle) {
    e.preventDefault();

    const w = 600;
    const h = 400;
    const left = Math.round((window.screen.width / 2) - (w / 2));
    const top = Math.round((window.screen.height / 2) - (h / 2));

    let _url = '';
    if (platform === 'twitter') {
      _url = `https://twitter.com/intent/tweet?text=${title}&url=${encodeURIComponent(url)}${twitterHandle && (`&via=${twitterHandle.replace('@', '')}`)}`;
    } else if (platform === 'facebook') {
      _url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    } else if (platform === 'linkedin') {
      _url = `https://www.linkedin.com/shareArticle?mini=true&title=${title}&url=${encodeURIComponent(url)}`;
    }

    const dialog = window.open(
      _url,
      platform,
      `window.open(${_url},"_blank", scrollbars=yes,resizable=yes,toolbar=no,location=yes,width=${w}, height=${h}, top=${top}, left=${left}, screenY=${top}, screenX=${left},resizable=1);`,
    );
    dialog.moveTo(left, top);
    dialog.focus();
  }

  /**
   * Get href link.
   */
  getHref(singlePlatform) {
    const { title, url } = this.props;

    // Check if we are emailing
    if (singlePlatform.platform === 'email') {
      return `mailto:?subject=${encodeURIComponent(singlePlatform.title || title)}&body=${url}`;
    }

    // Check if we have a global url
    if (url.length) {
      return url;
    }

    // finally returning the url
    return singlePlatform.url;
  }

  /**
   * Renders the amp version of the component.
   */
  amp() {
    return renderAmp.bind(this)();
  }

  /**
   * Renders the component
   */
  render() {
    const {
      context: {
        cssPrefix,
        globalSettings,
      },
      extraClassNames,
      loadStyles,
      platforms,
      title,
      url,
    } = this.props;

    /*
    * if url and title have been set for social component, sharing option will be used
    * otherwise, user will be redirected to platform assigned URL
    */
    return (
      <div
        className={classNames({
          [`${cssPrefix}__social`]: loadStyles,
          ...extraClassNames,
        })}
        key={uniqid('social-')}
      >
        {platforms.map((singlePlatform, index) => (
          <Link
            extraClassNames={{
              [`${cssPrefix}__social--${singlePlatform.platform}`]: true,
            }}
            href={this.getHref(singlePlatform)}
            key={`singlePlatform-${index}`}
            onClick={(e) => {
              if (url.length && (singlePlatform.platform !== 'email')) {
                Social.handleClick(e, url, (singlePlatform.title || title), singlePlatform.platform, ((globalSettings && globalSettings.twitterHandle) || ''));
              }
            }}
            screenReaderText={singlePlatform.label ? singlePlatform.label : ''}
            target={!url.length ? '_blank' : undefined}
          >
            <span className={`${cssPrefix}__social--icon`}>
              <Icon icon={`${cssPrefix}__ico-${singlePlatform.platform.toLowerCase()}`} />
            </span>

            {singlePlatform.label && singlePlatform.showLabel && (
              <span className={`${cssPrefix}__social--label`}>{singlePlatform.label}</span>
            )}
          </Link>
        ))}
      </div>
    );
  }
}

Social.propTypes = {
  ampEnable: PropTypes.bool, // eslint-disable-line
  ampScripts: PropTypes.shape(), // eslint-disable-line
  context: PropTypes.shape(),
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  loadStyles: PropTypes.bool,
  platforms: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    platform: PropTypes.string,
    showLabel: PropTypes.bool,
    url: PropTypes.string,
  })),
  title: PropTypes.string,
  url: PropTypes.string,
};

Social.defaultProps = {
  ampEnable: true,
  ampScripts: {
    'amp-social-share': 'https://cdn.ampproject.org/v0/amp-social-share-0.1.js',
  },
  context: {},
  extraClassNames: {},
  loadStyles: true,
  platforms: [],
  title: '',
  url: '',
};

export default compose(withPolaris, withAmp)(Social);
